import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiRequestError, canAny } from '../../api/client';
import { fetchReportQuota, generateReport, reportFileUrl, waitForReport } from '../../api/ops';
import { usePrefs } from '../../app/prefs';
import { Field } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useMe } from '../auth/session';

const TYPES: api.ReportType[] = ['functioning', 'energy_annual', 'light_points', 'monitoring_annual'];

/**
 * "Generează raport" (AFM point j): one prominent button that produces an up-to-date report now (stored, with
 * PDF / XLSX / CSV), then offers the downloads. The authority observer ("AFM", no operations.view) is taken instead to
 * the report itself, PDF only, in the same page (/reports/view/:id, user 2026-10-10); other roles keep the file links. Available to every holder of report.generate, the authority observer
 * included. Accounts with a quota (the observer: 5 a day, 50 kept) see their counters; at 50 kept the oldest report is
 * named and replaced only after the user confirms it (user decision 2026-10-09).
 */
export function GenerateReport({
  clientId,
  projectId,
  defaultType = 'functioning',
  compact = false,
}: {
  clientId: string | null;
  projectId: string | null;
  defaultType?: api.ReportType;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const portal = !canAny(useMe().data, 'operations.view');
  const locale = usePrefs((s) => s.locale);
  const [type, setType] = useState<api.ReportType>(defaultType);
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [result, setResult] = useState<api.Report | null>(null);
  const quota = useQuery({ queryKey: ['reports', 'quota'], queryFn: fetchReportQuota });
  const q = quota.data?.limited ? quota.data : null;
  const dailyDone = !!q && q.used_today >= q.daily_limit;
  const full = !!q && q.total >= q.total_limit;
  const [confirmReplace, setConfirmReplace] = useState(false);
  const run = useMutation({
    mutationFn: async (replaceOldest: boolean) => {
      setResult(null);
      const r = await generateReport({
        ...(replaceOldest ? { replace_oldest: true } : {}),
        type,
        client_id: clientId!,
        ...(projectId ? { project_id: projectId } : {}),
        ...(type === 'energy_annual' || type === 'monitoring_annual' ? { year } : {}),
        locale,
      });
      return waitForReport(r.id);
    },
    onSuccess: (r) => {
      setConfirmReplace(false);
      void qc.invalidateQueries({ queryKey: ['reports'] });
      if (portal && r.status === 'ready') return void navigate(`/reports/view/${r.id}`);
      setResult(r);
    },
    onError: () => void qc.invalidateQueries({ queryKey: ['reports', 'quota'] }),
  });
  const code = run.error instanceof ApiRequestError ? run.error.code : null;
  const annual = type === 'energy_annual' || type === 'monitoring_annual';
  return (
    <div className="generate-report">
      {!compact && (
        <Field label={t('reportsPage.type')}>
          <select className="input" value={type} onChange={(e) => setType(e.target.value as api.ReportType)}>
            {TYPES.map((x) => (
              <option key={x} value={x}>
                {t(`reportsPage.types.${x}`)}
              </option>
            ))}
          </select>
        </Field>
      )}
      {!compact && annual && (
        <Field label={t('reportsPage.year')}>
          <input
            className="input"
            type="number"
            min={2015}
            max={new Date().getFullYear()}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </Field>
      )}
      {q && (
        <span className="small muted" role="status">
          {t('reportsPage.quota.counters', {
            today: q.used_today,
            daily: q.daily_limit,
            total: q.total,
            max: q.total_limit,
          })}
        </span>
      )}
      {full && !dailyDone && q?.oldest && (
        <div className="notice" role="alert">
          {t('reportsPage.quota.replaceWarning', {
            max: q.total_limit,
            title: q.oldest.title,
            date: formatDateTime(q.oldest.created_at),
          })}
          <label className="check-row" style={{ marginTop: 6 }}>
            <input
              type="checkbox"
              checked={confirmReplace}
              onChange={(e) => setConfirmReplace(e.target.checked)}
            />
            {t('reportsPage.quota.replaceConfirm')}
          </label>
        </div>
      )}
      {dailyDone && (
        <div className="notice" role="alert">
          {t('reportsPage.quota.dailyReached', { daily: q!.daily_limit })}
        </div>
      )}
      <button
        type="button"
        className="btn btn-primary btn-lg"
        disabled={!clientId || run.isPending || dailyDone || (full && !confirmReplace)}
        onClick={() => run.mutate(full && confirmReplace)}
      >
        <FileText size={17} />
        {run.isPending
          ? t('reportsPage.generating')
          : full
            ? t('reportsPage.quota.generateReplacing')
            : t('reportsPage.generate')}
      </button>
      {result?.status === 'ready' && (
        <span className="toolbar">
          {result.formats.map((f) => (
            <a key={f} className="btn" href={reportFileUrl(result.id, f)} download>
              <FileDown size={15} /> {f.toUpperCase()}
            </a>
          ))}
        </span>
      )}
      {/* an observer with no project ticked (U21) is told why, not just that it failed */}
      {code === 'NO_VISIBLE_PROJECTS' ? (
        <span className="form-error">{t('errors.NO_VISIBLE_PROJECTS')}</span>
      ) : (
        (result?.status === 'failed' || (run.isError && !code?.startsWith('REPORT_'))) && (
          <span className="form-error">{t('reportsPage.failed')}</span>
        )
      )}
    </div>
  );
}
